import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Mail, Lock, Eye, EyeOff, Phone } from 'lucide-react';
import { goToLogin } from '../utils/navigation';

const HomeRegistrationForm: React.FC = () => {
  //
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: 'DEVOTEE',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showOTPVerification, setShowOTPVerification] = useState(false);
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const { user, signup, resendOTP, verifyRegistrationOTP, loading, error, clearError } = useAuth();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    
    // Clear validation errors when user starts typing
    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: '' }));
    }
    
    if (error) {
      clearError();
    }
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};

    // First Name validation
    if (!formData.firstName.trim()) {
      errors.firstName = 'First name is required';
    }

    // Last Name validation
    if (!formData.lastName.trim()) {
      errors.lastName = 'Last name is required';
    }

    // Phone validation
    if (!formData.phone.trim()) {
      errors.phone = 'Phone number is required';
    } else if (!/^\+?[\d\s\-\(\)]+$/.test(formData.phone)) {
      errors.phone = 'Please enter a valid phone number';
    }

    // Email validation
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    // Password validation
    if (!formData.password) {
      errors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      errors.password = 'Password must be at least 8 characters long';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
      errors.password = 'Password must contain uppercase, lowercase, and number';
    }

    // Confirm password validation
    if (!formData.confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      await signup({
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone,
        email: formData.email,
        password: formData.password,
        role: formData.role,
      });
      
      // If registration is successful, redirect to home page
       setShowOTPVerification(true);
      // window.location.href = '/';
    } catch (err) {
      // Error is handled by the auth context
    }
  };

  const handleOTPVerificationSuccess = async () => {
    // Show registration complete message
    setShowOTPVerification(false);
    setRegistrationComplete(true);
    
    // Reset form after 3 seconds
    setTimeout(() => {
      setRegistrationComplete(false);
      setFormData({
        firstName: '',
        lastName: '',
        phone: '',
        email: '',
        password: '',
        confirmPassword: '',
        role: 'DEVOTEE',
      });
    }, 3000);
  };

  //

  // Show OTP verification (no page reload)
  if (showOTPVerification) {
    return (
      <HomeOTPVerification
        phone={formData.phone}
        onSuccess={handleOTPVerificationSuccess}
        verifyRegistrationOTP={verifyRegistrationOTP}
        resendOTP={resendOTP}
        loading={loading}
        error={error}
        clearError={clearError}
      />
    );
  }

  // Show registration complete message (no page reload)
  if (registrationComplete) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center">
          <h3 className="font-runtime text-xl font-bold text-black mb-2">Registration Successful!</h3>
          <p className="font-lucy-rose text-gray-600">Your account has been created successfully. You can now log in with your credentials.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="text-center mb-10">
        <h3 className="font-runtime text-4xl font-bold text-gray-900 mb-4">
          Join the Network
        </h3>
        <p className="font-lucy-rose text-gray-600 text-lg max-w-md mx-auto">
          Create your account and start exploring the cultural heritage
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div>
            <label htmlFor="home-firstName" className="block text-sm font-semibold text-gray-800 mb-2.5 font-lucy-rose">
              First Name *
            </label>
            <input
              type="text"
              id="home-firstName"
              name="firstName"
              required
              value={formData.firstName}
              onChange={handleChange}
              className={`w-full px-4 py-3.5 border rounded-xl bg-white/80 hover:bg-white focus:bg-white focus:ring-2 focus:ring-dpn-pink focus:border-transparent outline-none font-lucy-rose transition-all duration-300 ${
                validationErrors.firstName ? 'border-red-300 bg-red-50' : 'border-gray-200 hover:border-gray-300'
              }`}
              placeholder="Enter your first name"
            />
            {validationErrors.firstName && (
              <p className="mt-1 text-sm text-red-600 font-lucy-rose">{validationErrors.firstName}</p>
            )}
          </div>
          
          <div>
            <label htmlFor="home-lastName" className="block text-sm font-semibold text-gray-800 mb-2.5 font-lucy-rose">
              Last Name *
            </label>
            <input
              type="text"
              id="home-lastName"
              name="lastName"
              required
              value={formData.lastName}
              onChange={handleChange}
              className={`w-full px-4 py-3.5 border rounded-xl bg-white/80 hover:bg-white focus:bg-white focus:ring-2 focus:ring-dpn-pink focus:border-transparent outline-none font-lucy-rose transition-all duration-300 ${
                validationErrors.lastName ? 'border-red-300 bg-red-50' : 'border-gray-200 hover:border-gray-300'
              }`}
              placeholder="Enter your last name"
            />
            {validationErrors.lastName && (
              <p className="mt-1 text-sm text-red-600 font-lucy-rose">{validationErrors.lastName}</p>
            )}
          </div>
        </div>

        <div>
          <label htmlFor="home-phone" className="block text-sm font-semibold text-gray-800 mb-2.5 font-lucy-rose">
            Phone Number *
          </label>
          <div className="relative">
            <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <input
              type="tel"
              id="home-phone"
              name="phone"
              required
              value={formData.phone}
              onChange={handleChange}
              className={`w-full pl-10 pr-4 py-3.5 border rounded-xl bg-white/80 hover:bg-white focus:bg-white focus:ring-2 focus:ring-dpn-pink focus:border-transparent outline-none font-lucy-rose transition-all duration-300 ${
                validationErrors.phone ? 'border-red-300 bg-red-50' : 'border-gray-200 hover:border-gray-300'
              }`}
              placeholder="+1 (555) 123-4567"
            />
          </div>
          {validationErrors.phone && (
            <p className="mt-1 text-sm text-red-600 font-lucy-rose">{validationErrors.phone}</p>
          )}
        </div>

        <div>
          <label htmlFor="home-email" className="block text-sm font-semibold text-gray-800 mb-2.5 font-lucy-rose">
            Email Address *
          </label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <input
              type="email"
              id="home-email"
              name="email"
              required
              value={formData.email}
              onChange={handleChange}
              className={`w-full pl-10 pr-4 py-3.5 border rounded-xl bg-white/80 hover:bg-white focus:bg-white focus:ring-2 focus:ring-dpn-pink focus:border-transparent outline-none font-lucy-rose transition-all duration-300 ${
                validationErrors.email ? 'border-red-300 bg-red-50' : 'border-gray-300'
              }`}
              placeholder="your@email.com"
            />
          </div>
          {validationErrors.email && (
            <p className="mt-1 text-sm text-red-600 font-lucy-rose">{validationErrors.email}</p>
          )}
        </div>

        <div>
          <label htmlFor="home-role" className="block text-sm font-semibold text-gray-800 mb-2.5 font-lucy-rose">
            Role *
          </label>
          <select
            id="home-role"
            name="role"
            required
            value={formData.role}
            onChange={handleChange}
            className="w-full py-3.5 px-4 border border-gray-200 hover:border-gray-300 rounded-xl bg-white/80 hover:bg-white focus:bg-white focus:ring-2 focus:ring-dpn-pink focus:border-transparent outline-none font-lucy-rose transition-all duration-300"
          >
            <option value="DEVOTEE">Devotee</option>
            <option value="COMMITTEE">Committee Member</option>
            <option value="VENDOR">Vendor</option>
          </select>
        </div>

        <div>
          <label htmlFor="home-password" className="block text-sm font-semibold text-gray-800 mb-2.5 font-lucy-rose">
            Password *
          </label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <input
              type={showPassword ? 'text' : 'password'}
              id="home-password"
              name="password"
              required
              value={formData.password}
              onChange={handleChange}
              className={`w-full pl-10 pr-12 py-3 border rounded-lg focus:ring-2 focus:ring-white focus:border-transparent outline-none font-lucy-rose ${
                validationErrors.password ? 'border-red-300 bg-red-50' : 'border-gray-300'
              }`}
              placeholder="Create a password"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </div>
          {validationErrors.password && (
            <p className="mt-1 text-sm text-red-600 font-lucy-rose">{validationErrors.password}</p>
          )}
        </div>

        <div>
          <label htmlFor="home-confirm-password" className="block text-sm font-semibold text-gray-800 mb-2.5 font-lucy-rose">
            Confirm Password *
          </label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <input
              type={showConfirmPassword ? 'text' : 'password'}
              id="home-confirm-password"
              name="confirmPassword"
              required
              value={formData.confirmPassword}
              onChange={handleChange}
              className={`w-full pl-10 pr-12 py-3 border rounded-lg focus:ring-2 focus:ring-white focus:border-transparent outline-none font-lucy-rose ${
                validationErrors.confirmPassword ? 'border-red-300 bg-red-50' : 'border-gray-300'
              }`}
              placeholder="Confirm your password"
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </div>
          {validationErrors.confirmPassword && (
            <p className="mt-1 text-sm text-red-600 font-lucy-rose">{validationErrors.confirmPassword}</p>
          )}
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-red-600 text-sm font-lucy-rose">{error}</p>
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          className="w-full bg-gradient-to-r from-dpn-raspberry to-dpn-pink text-white font-lucy-rose font-semibold py-4 px-6 rounded-xl transition-all duration-300 transform hover:scale-[1.02] hover:shadow-[0_8px_25px_rgb(240,38,102,0.35)] shadow-[0_8px_15px_rgb(240,38,102,0.2)] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center"
        >
          {loading ? (
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
          ) : (
            <span className="mr-2">→</span>
          )}
          {loading ? 'Creating account...' : 'Join Network'}
        </button>
      </form>

      {!user && (
        <div className="mt-4 text-center">
          <p className="font-lucy-rose text-sm text-gray-500">
            Already have an account?{' '}
            <button
              onClick={goToLogin}
              className="text-dpn-pink hover:text-dpn-raspberry font-medium transition-colors"
            >
              Sign in
            </button>
          </p>
        </div>
      )}
    </div>
  );
};

// Home OTP Verification Component
interface HomeOTPVerificationProps {
  phone: string;
  onSuccess: () => void;
  verifyRegistrationOTP: (phone: string, otp: string) => Promise<any>;
  resendOTP: (phone: string) => Promise<any>;
  loading: boolean;
  error: string | null;
  clearError: () => void;
}

const HomeOTPVerification: React.FC<HomeOTPVerificationProps> = ({ 
  phone,
  onSuccess,
  verifyRegistrationOTP,
  resendOTP,
  loading,
  error,
  clearError
}) => {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [timeLeft, setTimeLeft] = useState(120); // 2 minutes
  const [isResending, setIsResending] = useState(false);

  // Timer countdown
  React.useEffect(() => {
    if (timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [timeLeft]);

  // Clear errors when component mounts
  React.useEffect(() => {
    clearError();
  }, [clearError]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleOtpChange = (index: number, value: string) => {
    if (value.length > 1) return; // Prevent multiple characters
    
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      const nextInput = document.getElementById(`home-otp-${index + 1}`);
      nextInput?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`home-otp-${index - 1}`);
      prevInput?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6);
    const newOtp = [...otp];
    
    for (let i = 0; i < pastedData.length && i < 6; i++) {
      if (/^\d$/.test(pastedData[i])) {
        newOtp[i] = pastedData[i];
      }
    }
    
    setOtp(newOtp);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const otpString = otp.join('');
    
    if (otpString.length !== 6) {
      return;
    }

    try {
      await verifyRegistrationOTP(phone, otpString);
      onSuccess();
    } catch (err) {
      // Error is handled by auth context
    }
  };

  const handleResendOTP = async () => {
    setIsResending(true);
    try {
      await resendOTP(phone);
      setTimeLeft(120);
      setOtp(['', '', '', '', '', '']);
    } catch (err) {
      // Error is handled by auth context
    } finally {
      setIsResending(false);
    }
  };

  const otpString = otp.join('');
  const isValid = otpString.length === 6;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 relative">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-dpn-pink bg-opacity-10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Mail className="h-8 w-8 text-dpn-pink" />
          </div>
          <h2 className="font-runtime text-2xl font-bold text-black mb-2">
            Verify Your Registration
          </h2>
          <p className="font-lucy-rose text-gray-600">
            We've sent a 6-digit code to
          </p>
          <p className="font-lucy-rose text-dpn-pink font-medium">
            {phone}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* OTP Input Fields */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-4 text-center font-lucy-rose">
              Enter verification code
            </label>
            <div className="flex justify-center space-x-3">
              {otp.map((digit, index) => (
                <input
                  key={index}
                  id={`home-otp-${index}`}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={handlePaste}
                  className="w-12 h-12 text-center text-2xl font-bold border border-gray-300 rounded-lg focus:ring-2 focus:ring-dpn-pink focus:border-transparent outline-none"
                />
              ))}
            </div>
          </div>

          {/* Timer and Resend */}
          <div className="text-center">
            {timeLeft > 0 ? (
              <p className="font-lucy-rose text-sm text-gray-600">
                Resend code in{' '}
                <span className="font-medium text-dpn-pink">
                  {formatTime(timeLeft)}
                </span>
              </p>
            ) : (
              <button
                type="button"
                onClick={handleResendOTP}
                disabled={isResending || loading}
                className="font-lucy-rose text-sm text-dpn-pink hover:text-dpn-raspberry font-medium transition-colors disabled:opacity-50 flex items-center justify-center mx-auto"
              >
                {(isResending || loading) ? 'Sending...' : 'Resend Code'}
              </button>
            )}
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-red-600 text-sm font-lucy-rose text-center">{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={!isValid || loading}
            className="w-full bg-dpn-pink hover:bg-dpn-raspberry text-white font-lucy-rose font-semibold py-3 px-6 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center"
          >
            {loading ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
            ) : null}
            {loading ? 'Verifying...' : 'Complete Registration'}
          </button>
        </form>

        <div className="mt-6 text-center">
          <p className="font-lucy-rose text-xs text-gray-500">
            Didn't receive the code? Check your WhatsApp or try again.
          </p>
        </div>
      </div>
    </div>
  );
};

export default HomeRegistrationForm;
